import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, MessageSquare, Plus, Trash2 } from 'lucide-react';
import { chatsApi, type Chat } from '@/lib/chats.api';
import { getApiErrorMessage } from '@/lib/http';
import { SidebarExpandButton } from '@/components/SidebarExpandButton';
import { RainbowBeamsBackdrop } from '@/components/RainbowBeamsBackdrop';

function formatRelative(updatedAt: string) {
    const then = new Date(updatedAt).getTime();
    const diff = Date.now() - then;
    if (Number.isNaN(diff) || diff < 0) return '';

    const minutes = Math.floor(diff / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(updatedAt).toLocaleDateString();
}

const STATUS_LABEL: Record<Chat['status'], string> = {
    active: 'Active',
    launched: 'Launched',
    archived: 'Archived',
};
const STATUS_FG: Record<Chat['status'], string> = {
    active: 'var(--text-secondary)',
    launched: 'var(--fg-success)',
    archived: 'var(--text-tertiary)',
};
const STATUS_BG: Record<Chat['status'], string> = {
    active: 'var(--surface-tertiary)',
    launched: 'var(--bg-success)',
    archived: 'var(--surface-tertiary)',
};

export function ChatsListPage() {
    const navigate = useNavigate();
    const [chats, setChats] = useState<Chat[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);
    const [pendingDelete, setPendingDelete] = useState<string | null>(null);

    const fetchChats = useCallback(async () => {
        setError(null);
        try {
            const { results } = await chatsApi.list();
            setChats(results);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Could not load chats.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void fetchChats();
    }, [fetchChats]);

    async function handleCreate() {
        if (creating) return;
        setCreating(true);
        try {
            const chat = await chatsApi.create();
            navigate(`/v2/chats/${chat._id}`);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Could not create a new chat.'));
            setCreating(false);
        }
    }

    async function handleDelete(chatId: string) {
        setPendingDelete(chatId);
        try {
            await chatsApi.archive(chatId);
            setChats((current) => current.filter((c) => c._id !== chatId));
        } catch (err) {
            setError(getApiErrorMessage(err, 'Could not archive chat.'));
        } finally {
            setPendingDelete(null);
        }
    }

    return (
        <div
            style={{
                width: '100%',
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--surface-primary)',
                fontFamily: 'var(--font-sans)',
                color: 'var(--text-primary)',
                position: 'relative',
                isolation: 'isolate',
                overflow: 'hidden',
            }}
        >
            <RainbowBeamsBackdrop />
            <header
                style={{
                    padding: '32px 40px 24px',
                    borderBottom: '1px solid var(--border-muted)',
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 16,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <SidebarExpandButton style={{ marginTop: 4, marginLeft: -8 }} />
                    <div>
                        <h1
                            style={{
                                fontFamily: 'var(--font-serif)',
                                fontSize: 32,
                                fontWeight: 500,
                                letterSpacing: '-0.01em',
                                margin: 0,
                            }}
                        >
                            Chats
                        </h1>
                        <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                            Conversations with Lantern. Each chat can spin up a new role end-to-end.
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    onClick={handleCreate}
                    disabled={creating}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '8px 14px',
                        background: 'var(--accent)',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: 9999,
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: creating ? 'wait' : 'pointer',
                        opacity: creating ? 0.7 : 1,
                    }}
                >
                    {creating ? <Loader2 size={14} strokeWidth={2} className="animate-spin" /> : <Plus size={14} strokeWidth={2} />}
                    New chat
                </button>
            </header>

            {error ? (
                <div
                    style={{
                        margin: '16px 40px 0',
                        padding: '10px 14px',
                        background: 'var(--bg-danger)',
                        color: 'var(--fg-danger)',
                        borderRadius: 8,
                        fontSize: 13,
                    }}
                >
                    {error}
                </div>
            ) : null}

            {loading ? (
                <div
                    style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--text-tertiary)',
                    }}
                >
                    <Loader2 size={18} strokeWidth={1.75} className="animate-spin" />
                </div>
            ) : chats.length === 0 ? (
                <EmptyState />
            ) : (
                <div style={{ padding: '16px 40px 40px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {chats.map((chat) => (
                        <ChatRow
                            key={chat._id}
                            chat={chat}
                            isDeleting={pendingDelete === chat._id}
                            onOpen={() => navigate(`/v2/chats/${chat._id}`)}
                            onDelete={() => handleDelete(chat._id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function EmptyState() {
    return (
        <div
            style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 40,
            }}
        >
            <div style={{ textAlign: 'center', maxWidth: 420, color: 'var(--text-tertiary)' }}>
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: 'var(--surface-tertiary)',
                        color: 'var(--text-secondary)',
                        marginBottom: 16,
                    }}
                >
                    <MessageSquare size={20} strokeWidth={1.6} />
                </span>
                <p style={{ margin: 0, fontSize: 15, color: 'var(--text-primary)', fontWeight: 500 }}>No chats yet</p>
                <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    Start a new chat to spin up a role with Lantern. Each chat captures the full
                    conversation, the live artifacts, and the launched job.
                </p>
            </div>
        </div>
    );
}

function ChatRow({
    chat,
    isDeleting,
    onOpen,
    onDelete,
}: {
    chat: Chat;
    isDeleting: boolean;
    onOpen: () => void;
    onDelete: () => void;
}) {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                border: '1px solid var(--border-muted)',
                borderRadius: 12,
                background: 'var(--surface-primary)',
                transition: 'border-color 120ms',
            }}
        >
            <button
                type="button"
                onClick={onOpen}
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: 0,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    color: 'inherit',
                    minWidth: 0,
                }}
            >
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: 'var(--surface-tertiary)',
                        color: 'var(--text-secondary)',
                        flexShrink: 0,
                    }}
                >
                    <MessageSquare size={15} strokeWidth={1.7} />
                </span>
                <span style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <strong
                        style={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: 'var(--text-primary)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {chat.title || 'Untitled chat'}
                    </strong>
                    <span
                        style={{
                            fontSize: 12,
                            color: 'var(--text-tertiary)',
                            marginTop: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        <span>{formatRelative(chat.updatedAt)}</span>
                        {chat.jobId ? (
                            <>
                                <span>·</span>
                                <span style={{ color: 'var(--text-secondary)' }}>Job linked</span>
                            </>
                        ) : null}
                    </span>
                </span>
                <span
                    style={{
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        padding: '3px 8px',
                        borderRadius: 9999,
                        background: STATUS_BG[chat.status],
                        color: STATUS_FG[chat.status],
                        flexShrink: 0,
                    }}
                >
                    {STATUS_LABEL[chat.status]}
                </span>
            </button>
            <button
                type="button"
                onClick={onDelete}
                disabled={isDeleting}
                aria-label={`Archive ${chat.title || 'chat'}`}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-tertiary)',
                    cursor: isDeleting ? 'wait' : 'pointer',
                    opacity: isDeleting ? 0.5 : 1,
                    flexShrink: 0,
                }}
            >
                {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} strokeWidth={1.7} />}
            </button>
        </div>
    );
}
